import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { subMonths } from 'date-fns'
import { Subject, debounceTime, takeUntil } from 'rxjs'
import { getTimeZoneLabel, floorToMinutes } from '../../utils/date.utils'

@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  standalone: false,
})
export class DatetimePickerComponent implements OnInit, OnDestroy, OnChanges {
  @Output() datetimeChanged = new EventEmitter<Date>()
  datetimeChanged$ = new Subject<void>()
  cleanUp$ = new Subject<void>()
  minDate = subMonths(new Date(Date.now()), 3)
  maxDate = new Date(Date.now())
  @Input() datetime: Date | string | undefined
  @Input() step: number = 15
  @Input() disabled = false
  @Input() enableTime = true
  public timezone: string | undefined

  constructor() {}

  private parseDatetime(datetime: Date | string | undefined): Date | undefined {
    if (!datetime) return undefined

    const parsedDatetime = datetime instanceof Date ? datetime : new Date(datetime)
    if (Number.isNaN(parsedDatetime.getTime())) return undefined

    return floorToMinutes(parsedDatetime, this.step)
  }

  ngOnInit(): void {
    const parsedDatetime = this.parseDatetime(this.datetime)
    if (parsedDatetime) {
      this.datetime = parsedDatetime
      this.timezone = getTimeZoneLabel(parsedDatetime)
    }

    this.datetimeChanged$.pipe(debounceTime(500), takeUntil(this.cleanUp$)).subscribe(() => {
      const changedDatetime = this.parseDatetime(this.datetime)

      if (changedDatetime) {
        this.timezone = getTimeZoneLabel(changedDatetime)
        this.datetimeChanged.emit(changedDatetime)
      }
    })
  }

  ngOnDestroy(): void {
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['datetime']) {
      const newValue = changes['datetime'].currentValue
      const parsed = this.parseDatetime(newValue)
      if (parsed) {
        this.datetime = parsed
        this.timezone = getTimeZoneLabel(parsed)
      }
    }
  }

  onChange(time: Date | string | undefined) {
    if (time) {
      const parsed = this.parseDatetime(time)
      if (parsed) {
        this.datetime = parsed
      }
    } else {
      this.datetime = time
    }
    this.datetimeChanged$.next()
  }
}
